import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Eye, EyeOff, CheckCircle, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const { login, register, resendVerificationEmail } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (result.emailVerificationRequired) {
          setShowVerificationMessage(true);
        } else {
          onClose();
        }
      } else {
        const result = await register(email, password, name);
        if (result.emailVerificationRequired) {
          setShowVerificationMessage(true);
        } else {
          onClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    const result = await resendVerificationEmail(email);
    setResendLoading(false);
    if (result.success) {
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    }
  };

  const handleClose = () => {
    setShowVerificationMessage(false);
    setResendSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={handleClose} data-testid="auth-modal-overlay">
        <motion.div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          data-testid="auth-modal"
        >
          <button className="modal-close" onClick={handleClose} data-testid="close-modal-btn">
            <X size={20} />
          </button>

          {showVerificationMessage ? (
            // Verification Email Sent View
            <div className="verification-sent">
              <div className="verification-icon">
                <Mail size={48} />
              </div>
              <h2>Check Your Email</h2>
              <p className="modal-subtitle">
                We've sent a verification link to <strong>{email}</strong>
              </p>
              <p className="verification-note">
                Please click the link in the email to verify your account. 
                The link will expire in 24 hours.
              </p>
              
              <div className="verification-actions">
                <button
                  className="btn btn-outline resend-btn"
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendSuccess}
                  data-testid="resend-verification-btn"
                >
                  {resendLoading ? (
                    'Sending...'
                  ) : resendSuccess ? (
                    <>
                      <CheckCircle size={16} />
                      Email Sent!
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Resend Email
                    </>
                  )}
                </button>
                
                <button
                  className="btn btn-primary"
                  onClick={handleClose}
                >
                  Continue to Site
                </button>
              </div>
              
              <p className="verification-tip">
                Didn't receive the email? Check your spam folder or click resend above.
              </p>
            </div>
          ) : (
            // Login/Register Form
            <>
              <h2>{mode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
              <p className="modal-subtitle">
                {mode === 'login'
                  ? 'Sign in to access your account'
                  : 'Start your trading journey today'}
              </p>

              {error && <div className="error-message" data-testid="auth-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                {mode === 'register' && (
                  <div className="input-group">
                    <label>Full Name</label>
                    <div className="input-wrapper">
                      <User size={18} />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Smith"
                        required
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                )}

                <div className="input-group">
                  <label>Email</label>
                  <div className="input-wrapper">
                    <Mail size={18} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Password</label>
                  <div className="input-wrapper">
                    <Lock size={18} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary submit-btn"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>
                
                {mode === 'login' && (
                  <Link to="/forgot-password" className="forgot-password-link" onClick={handleClose} data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                )}
              </form>

              <div className="modal-footer">
                {mode === 'login' ? (
                  <p>
                    Don't have an account?{' '}
                    <button onClick={() => setMode('register')} data-testid="switch-to-register">
                      Sign up
                    </button>
                  </p>
                ) : (
                  <p>
                    Already have an account?{' '}
                    <button onClick={() => setMode('login')} data-testid="switch-to-login">
                      Sign in
                    </button>
                  </p>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .modal-content {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
          max-width: 420px;
          width: 100%;
          position: relative;
        }

        .modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px;
        }

        .modal-close:hover {
          color: var(--text-primary);
        }

        .modal-content h2 {
          font-size: 1.75rem;
          margin-bottom: 8px;
        }

        .modal-subtitle {
          color: var(--text-muted);
          margin-bottom: 24px;
        }

        .modal-subtitle strong {
          color: var(--primary);
        }

        .error-message {
          background: var(--error-bg);
          color: var(--error);
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
        }

        .modal-content .input-group {
          margin-bottom: 16px;
        }

        .modal-content label {
          display: block;
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .modal-content .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .modal-content .input-wrapper svg {
          position: absolute;
          left: 12px;
          color: var(--text-muted);
        }

        .modal-content .input-wrapper input {
          width: 100%;
          padding: 12px 12px 12px 40px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 16px;
        }

        .modal-content .input-wrapper input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .toggle-password {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px;
        }

        .submit-btn {
          width: 100%;
          padding: 14px;
          font-size: 16px;
          margin-top: 8px;
          border-radius: 8px;
        }

        .forgot-password-link {
          display: block;
          text-align: center;
          margin-top: 16px;
          color: var(--text-muted);
          font-size: 14px;
          text-decoration: none;
        }

        .forgot-password-link:hover {
          color: var(--primary);
        }

        .modal-footer {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--border);
        }

        .modal-footer p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .modal-footer button {
          background: none;
          border: none;
          color: var(--primary);
          cursor: pointer;
          font-weight: 500;
        }

        .modal-footer button:hover {
          text-decoration: underline;
        }

        /* Verification Sent Styles */
        .verification-sent {
          text-align: center;
        }

        .verification-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .verification-note {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .verification-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .verification-actions .btn {
          width: 100%;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-outline:hover:not(:disabled) {
          border-color: var(--primary);
          color: var(--primary);
        }

        .btn-outline:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .verification-tip {
          font-size: 13px;
          color: var(--text-muted);
        }
      `}</style>
    </AnimatePresence>
  );
};

export default AuthModal;
